import React from 'react'
import { Box, Typography } from '@mui/material'
import {
	ComparisonContent,
	DecisionMark,
	FeaturesBlock,
	PriceContent
} from 'Pages/AnalyzersV2/@components/Table/columns/Data/@components/'
import AnalyzerPopover from "Pages/AnalyzersV2/@components/Popover"
import { useCellColors, useCellDecisions, useCellPopover } from "Pages/AnalyzersV2/@components/Table/columns/Data/@hooks"
import { isEmpty } from "@ui-seeker/utils"
import { dsColorNew } from "@palette"
import {
	analyzerFilters,
	openEvolutionModal,
	tableHasMainComparison,
	tableHideDetails
} from "Pages/AnalyzersV2/@signals"
import { cellEvidence, isMainRow, rankCellEvidence, rowLabelOf } from "Pages/AnalyzersV2/@services/askEvidence"
import { analyzerAskContext } from "Pages/AnalyzersV2/@services/askScope"

// `rows` / `columns` (CU-869fkdhjv phase 3): the table the cell sits in, so "Ask Iris" can say where the cell stands
// in its column; `label` is the column key the response indexes the row by
export default function dataColumn(label, evolutionData, { isRating = false, isPosition = false, rows = [], columns = [] } = {}) {
	return {
		name: label,
		label: label,
		render: (value, row) => <RenderDataCell value={value} isRating={isRating} isPosition={isPosition} column={label} row={row} rows={rows} columns={columns}/>
	}
}

function RenderDataCell({ value, isRating, isPosition, column, row, rows, columns }) {
	const {
		price_data, price, previous_price, evolutionChange, isCheapestColumn,
		isMoreExpensiveColumn, noResult, evolutionDiff, comparison_type,
		diff_price, name, evolutionFilter, main_name
	} = value

	const { bgColor, borderColor, hoverBorderColor } = useCellColors(isCheapestColumn, isMoreExpensiveColumn)
	const { isOpenPopover, onClosePopover, onOpenPopover } = useCellPopover(noResult)
	// the engine's decisions of the open pass on this cell (CU-869fmm949); none with a report
	const cellDecisions = useCellDecisions(price_data)
	const rowLabel = rowLabelOf(row) || name

	// "Ask Iris about this price" (CU-869fkdhjv phase 3): the evidence of the moment, built at click time
	const getAsk = () => {
		const ctx = analyzerAskContext({ currency: price_data?.id_master_currency_translation, mainName: main_name })
		// Rank Analyzer: the brand × date cell, against the main brand of the table
		if (isPosition) return rankCellEvidence({
			cell: value, column, rowLabel, rows, mainName: rowLabelOf((rows || []).find(isMainRow)) || main_name,
			currency: price_data?.id_master_currency_translation, lor: ctx.lor, params: ctx.params, freshness: ctx.freshness,
		})
		return cellEvidence({
			section: ctx.section, cell: value, column, rowLabel, rows, columns,
			currency: price_data?.id_master_currency_translation, daily: ctx.params?.scope?.price === 'daily',
			minMax: ctx.params?.scope?.table,
			// only what is painted: the marks' switch on (plan-review risk, `cellDecisions.on`)
			decisions: cellDecisions.on ? cellDecisions.decisions : [],
			dsRepricing: ctx.dsRepricing, params: ctx.params, freshness: ctx.freshness,
		})
	}
	const askOrigin = { row: rowLabel, car: price_data?.id_acriss || price_data?.acriss || price_data?.id_category, date: price_data?.pickup_date, days: price_data?.length_of_rental, from: 'cell' }
	const hideDetails = tableHideDetails.value
	const hasValue = !isEmpty(value)
	const clickable = !noResult && hasValue
	// Position answers one question per cell — how this price sits against the main
	// brand — so it always carries the gap and never the previous price, the
	// evolution or the features, whatever the details toggle says.
	const showComparison = isPosition || !hideDetails
	const isCompact = isPosition || hideDetails

	const handleOpenEvolutionModal = ({ currentTarget }) => {
		if (!isRating) return onOpenPopover(currentTarget)

		openEvolutionModal.value = {
			price_data: evolutionFilter,
			filters: analyzerFilters.value,
			tableRows: [price_data]
		}
	}

	// The popover sits beside the clickable box, not inside it: React bubbles its clicks (the outside click
	// that closes it included) through the portal up to that box, which would open it again.
	return <>
	<Box onClick={handleOpenEvolutionModal}
				// reachable with the keyboard: Tab, Enter opens the detail, Esc closes it (UX Obs. 20)
				tabIndex={clickable ? 0 : undefined}
				role={clickable ? 'button' : undefined}
				onKeyDown={e => { if (clickable && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleOpenEvolutionModal(e) } }}
				sx={{
					'&:focus-visible > div': { outline: `2px solid ${dsColorNew.appBlue}`, outlineOffset: 1 },
					cursor: clickable ? 'pointer' : 'default',
					display: 'flex', alignItems: 'center', justifyContent: 'center',
					...clickable && { '&:hover > div': { borderColor: hoverBorderColor } }}}>
		<Box
			sx={{
				position: 'relative',
				border: '1px solid',
				borderColor: borderColor,
				borderRadius: '8px',
				transition: 'border-color 0.15s ease',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				flexDirection: 'column',
				backgroundColor: bgColor,
				...!clickable && { borderStyle: 'dashed', backgroundColor: 'transparent', color: dsColorNew.textSubtle },
				// The gap needs a little more room than a bare price, but nowhere near
				// the full detailed cell.
				width: isPosition ? 84 : (hideDetails ? 70 : 130),
				minHeight: isCompact ? undefined : (tableHasMainComparison.value ? 125 : 77)
			}}>
			{cellDecisions.on && <DecisionMark decisions={cellDecisions.decisions}/>}
			<Box
				sx={{
					width: isCompact ? '100%' : 110,
					m: isCompact ? 0 : 2
				}}>
				{clickable ? <PriceContent
						prevPrice={previous_price}
						price={price}
						evolutionChange={evolutionChange}
						evolutionDiff={evolutionDiff}
						currency={price_data?.id_master_currency_translation}
						compact={isPosition}
						previousPass={value.previous_pass}
					/>
					: <Typography sx={{ m: isCompact ? 0 : 1.2, textAlign: 'center', color: 'inherit' }} variant="subtitle1">N/A</Typography>}
				{showComparison && <ComparisonContent
					comparison_type={comparison_type || 'noComparison'}
					diff_price={diff_price}
					name={name}
					mainName={main_name}
					clickable={clickable}
					compact={isPosition}
				/>}

				{!isCompact && <FeaturesBlock cellData={price_data} noResult={noResult} />}
			</Box>
		</Box>
	</Box>
	{isOpenPopover && hasValue && <AnalyzerPopover
		isOpen={isOpenPopover}
		onClose={onClosePopover}
		data={value}
		decisions={cellDecisions.on ? cellDecisions : null}
		isRating={isRating}
		hideEvolution={isPosition}
		getAsk={getAsk}
		askOrigin={askOrigin}
	/>}
	</>
}
